import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getModbusTcpPUTDto } from 'src/Libs/flow/parsers'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { Box, Stack, Typography } from '@mui/material'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'
import { SettingInfoBox } from 'src/Elements/Data'
import { ControlledModbusTcpPUTDto, updateEvse } from '../../api/evse.api.js'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import useAppRatingContext from 'src/Libs/app-rating/hooks/use-app-rating-context.js'
import { usePortalMutation } from 'src/Libs/api'

type PhaseManagementValue = 'AutoPhaseSwitch' | '3PhBlocked' | '1PhBlocked'

type PhaseOption = {
  value: PhaseManagementValue
  labelKey: string
  descriptionKey: string
}

const PHASE_CONFIG: PhaseOption[] = [
  {
    value: 'AutoPhaseSwitch',
    labelKey: 'user-settings.evse-advanced-page.phase-management.options.automatic',
    descriptionKey: 'user-settings.evse-advanced-page.phase-management.options.automatic-description',
  },
  {
    value: '3PhBlocked',
    labelKey: 'user-settings.evse-advanced-page.phase-management.options.three-phase',
    descriptionKey: 'user-settings.evse-advanced-page.phase-management.options.three-phase-description',
  },
  {
    value: '1PhBlocked',
    labelKey: 'user-settings.evse-advanced-page.phase-management.options.single-phase',
    descriptionKey: 'user-settings.evse-advanced-page.phase-management.options.single-phase-description',
  },
]

export interface PhaseManagementEditorDialogProps {
  installationId: number
  queryIsLoading: boolean
  evse: ModbusTcpChargingStation
  isSinglePhase: boolean
  onClose?: () => void
}

/** Dialog to change the an EMC's phase option. ModbusTCP only. */
export const PhaseManagementEditorDialog: React.FC<PhaseManagementEditorDialogProps> = ({
  onClose,
  installationId,
  queryIsLoading,
  evse,
  isSinglePhase,
}) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { enqueueErrorStack } = useSnackbar()

  const currentPhaseManagement = evse.phaseManagement
  const [phaseManagement, setPhaseManagement] = useLatestState(currentPhaseManagement)

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: ControlledModbusTcpPUTDto) =>
      updateEvse(installationId, evse.rawData?.deviceLinkId?.toString() ?? '', evse.rawData?.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      })
      onClose?.()
    },
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setPhaseManagement(currentPhaseManagement)
    },
  })

  const isPhaseManagementSameAsInitial = phaseManagement === currentPhaseManagement
  const isLoading = queryIsLoading || mutateEvse.isPending
  const isLockedToSinglePhase = currentPhaseManagement === '1PhBlocked' && isSinglePhase

  const handleSave = (): void => {
    if (isPhaseManagementSameAsInitial) return
    mutateEvse.mutate(getModbusTcpPUTDto({ ...evse, phaseManagement }))
    triggerAppRating()
  }

  const isOptionDisabled = (option: PhaseOption): boolean => {
    if (isLoading || !userCanEdit) return true
    if (isLockedToSinglePhase && option.value !== '1PhBlocked') return true
    return false
  }

  const selectedConfig = PHASE_CONFIG.find(option => option.value === phaseManagement)
  const defaultTabIndex = PHASE_CONFIG.findIndex(option => option.value === currentPhaseManagement)

  const dialogActions: DialogActions = {
    primary: {
      name: t('common.save'),
      onClick: handleSave,
      disabled: isPhaseManagementSameAsInitial || !userCanEdit || mutateEvse.isPending,
      isLoading: queryIsLoading || mutateEvse.isPending,
    },
    secondary: {
      name: t('common.cancel'),
      onClick: onClose,
    },
  }

  return (
    <Dialog title={t('user-settings.evse-advanced-page.phase-management.title')} maxWidth="md" actions={dialogActions}>
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            bgcolor: 'shades.shade04',
            border: '1px solid shades.shade03',
            borderRadius: 1,
            gap: 1,
            px: 4,
            py: 2,
          }}
        >
          <Typography variant="h3">{t('user-settings.evse-advanced-page.phase-management.options.title')}</Typography>

          <TabButtonGroupBase sx={{ borderRadius: '100px' }} defaultTabIndex={defaultTabIndex > -1 ? defaultTabIndex : 0}>
            <TabButton onClick={() => setPhaseManagement('AutoPhaseSwitch')} isDisabled={isOptionDisabled(PHASE_CONFIG[0])}>
              {t(PHASE_CONFIG[0].labelKey)}
            </TabButton>
            <TabButton onClick={() => setPhaseManagement('3PhBlocked')} isDisabled={isOptionDisabled(PHASE_CONFIG[1])}>
              {t(PHASE_CONFIG[1].labelKey)}
            </TabButton>
            <TabButton onClick={() => setPhaseManagement('1PhBlocked')} isDisabled={isOptionDisabled(PHASE_CONFIG[2])}>
              {t(PHASE_CONFIG[2].labelKey)}
            </TabButton>
          </TabButtonGroupBase>
        </Box>

        {selectedConfig && <SettingInfoBox title={t(selectedConfig.labelKey)} description={t(selectedConfig.descriptionKey)} />}
      </Stack>
    </Dialog>
  )
}
